import { RocketIcon } from '@/components/icons/RocketIcon'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { authClient } from '@/lib/auth/client'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { zodValidator } from '@tanstack/zod-adapter'
import { Mail, RefreshCw } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import z from 'zod'

export const Route = createFileRoute('/verify-email')({
  component: RouteComponent,
  validateSearch: zodValidator(
    z.object({
      token: z.string().optional(),
    }),
  ),
  beforeLoad: async ({ search }) => {
    if (search.token) {
      // If there's a token in the params, try verify
      await authClient.verifyEmail({
        query: {
          token: search.token,
        },
      })
      // go to dashboard
      redirect({ to: '/', throw: true, replace: true })
    }
    const session = await authClient.getSession()
    if (!session.data || session.data.user.emailVerified) {
      redirect({ to: '/', throw: true, replace: true })
    }
  },
})

function RouteComponent() {
  const { data: session } = authClient.useSession()
  const navigate = useNavigate()
  const [isResending, setIsResending] = useState(false)

  const handleResendEmail = async () => {
    setIsResending(true)
    try {
      if (session?.user.email === undefined) {
        throw new Error('User email is undefined')
      }
      await authClient.sendVerificationEmail({
        email: session.user.email,
        callbackURL: window.location.origin,
      })
      toast.success('Verification email sent! Please check your inbox.')
    } catch (error) {
      console.error('Failed to resend verification email:', error)
      toast.error('Failed to send verification email. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  const handleSignOut = async () => {
    await authClient.signOut()
    navigate({ to: '/signin' })
  }

  return (
    <div className="flex justify-center items-center p-4 min-h-screen">
      <div className="w-full max-w-md">
        {/* Logo and Title */}
        <div className="mb-8 text-center">
          <div className="flex justify-center mb-4">
            <RocketIcon className="w-12 h-12 text-blue-600" />
          </div>
          <h1 className="mb-2 text-3xl font-bold text-gray-900">
            Verify Your Email
          </h1>
          <p className="text-gray-600">
            We've sent a verification link to your email
          </p>
        </div>

        {/* Verify Email Card */}
        <Card className="border-0 shadow-xl">
          <CardHeader className="space-y-1">
            <div className="flex justify-center mb-4">
              <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
                <Mail className="w-8 h-8 text-blue-600" />
              </div>
            </div>
            <CardTitle className="text-2xl text-center">
              Check Your Inbox
            </CardTitle>
            <CardDescription className="text-center">
              We sent a verification email to
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Display the user's email */}
            <div className="p-3 text-center bg-gray-50 rounded-lg">
              <p className="font-medium text-gray-900">{session?.user.email}</p>
            </div>

            {/* Instructions */}
            <div className="space-y-2 text-sm text-gray-600">
              <p>Click the link in the email to verify your account.</p>
              <p>
                If you don't see the email, check your spam folder or request a
                new one.
              </p>
            </div>

            {/* Resend Email Button */}
            <Button
              onClick={handleResendEmail}
              disabled={isResending}
              variant="outline"
              className="w-full"
            >
              {isResending ? (
                <>
                  <RefreshCw className="mr-2 w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                'Resend Email'
              )}
            </Button>

            {/* Sign Out Button */}
            <Button
              onClick={handleSignOut}
              variant="ghost"
              className="w-full text-gray-600 hover:text-gray-900"
            >
              Sign Out
            </Button>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
